import React from "react";
import {
  ITableItemApprovalStatusProps,
  ITableItemStatusProps,
} from "./definitions";
import StatusIndicator from "@amzn/awsui-components-react/polaris/status-indicator";

const TableItemStatus: React.FC<ITableItemStatusProps> = ({ stringStatus }) => {
  switch (stringStatus) {
    //status for data query
    case "yet to start":
      return <StatusIndicator type={"pending"}>Yet to start</StatusIndicator>;
    case "pending approval":
      return (
        <StatusIndicator type={"pending"}>Pending approval</StatusIndicator>
      );
    case "running":
      return (
        <StatusIndicator type={"in-progress"}>In progress</StatusIndicator>
      );
    case "error":
      return <StatusIndicator type={"error"}>Error</StatusIndicator>;
    case "rejected":
      return <StatusIndicator type={"error"}>Rejected</StatusIndicator>;
    case "success":
      return <StatusIndicator>Complete</StatusIndicator>;

    //status for task/audit
    case "Not started":
      return (
        <StatusIndicator type={"pending"} colorOverride="grey">
          Not started
        </StatusIndicator>
      );
    case "In appeal":
      return (
        <StatusIndicator type={"in-progress"} colorOverride="yellow">
          In appeal
        </StatusIndicator>
      );
    case "In progress":
      return (
        <StatusIndicator type={"in-progress"} colorOverride="blue">
          In progress
        </StatusIndicator>
      );
    case "On hold":
      return (
        <StatusIndicator type={"error"} colorOverride="red">
          On hold
        </StatusIndicator>
      );
    case "Complete":
      return <StatusIndicator colorOverride="green">Complete</StatusIndicator>;
    case "No status found":
      return (
        <StatusIndicator type={"warning"}>No status found</StatusIndicator>
      );
    //status for reviews and approval
    case "Pending":
      return <StatusIndicator type={"pending"}>Pending</StatusIndicator>;
    case "Rejected":
      return <StatusIndicator type={"error"}>Rejected</StatusIndicator>;
    case "Approved":
      return <StatusIndicator type={"success"}>Approved</StatusIndicator>;
    case "Skipped":
      return <StatusIndicator type={"stopped"}>Skipped</StatusIndicator>;
    //status for tax areas
    case "Active":
      return <StatusIndicator type={"success"}>Active</StatusIndicator>;
    case "Inactive":
      return <StatusIndicator type={"stopped"}>Inactive</StatusIndicator>;

    default:
      return (
        <StatusIndicator type={"in-progress"} colorOverride="blue">
          {stringStatus || "In progress"}
        </StatusIndicator>
      );
  }
};

/**
 * This is to get the status indicator for approval overall status. It will be shared between approval review table in Task overview page and approvals page table
 * @param param0
 * @returns
 */
export const TableItemApprovalStatus: React.FC<
  ITableItemApprovalStatusProps
> = ({ stringStatus, progress }) => {
  switch (stringStatus) {
    //approval approval overall status
    case "PENDING":
      if (progress) {
        return (
          <StatusIndicator type={"pending"}>
            In progress ({progress})
          </StatusIndicator>
        );
      }
      return <StatusIndicator type={"pending"}>In progress</StatusIndicator>;
    case "REJECTED":
      return <StatusIndicator type={"error"}>Rejected</StatusIndicator>;
    case "APPROVED":
      return <StatusIndicator type={"success"}>Approved</StatusIndicator>;
    case "ON_HOLD":
      return <StatusIndicator type={"pending"}>On hold</StatusIndicator>;
    case "INVALIDATED":
      return <StatusIndicator type={"error"}>Cancelled</StatusIndicator>;
    case "CANCELLED":
      return <StatusIndicator type={"error"}>Cancelled</StatusIndicator>;

    default:
      return <StatusIndicator type={"in-progress"}>Unknown</StatusIndicator>;
  }
};

/**
 * This is to get the status indicator for approval reviewer(approver) status. The backend approver status has been transformed for rendering: /Amazon-GaiaUI/src/components/approval-review-table/util.ts
 * @param param0
 * @returns
 */
export const TableItemApproverStatus: React.FC<ITableItemStatusProps> = ({
  stringStatus,
}) => {
  switch (stringStatus) {
    //approval review table approver status
    case "PENDING":
      return <StatusIndicator type={"pending"}>Pending</StatusIndicator>;
    case "REWORK":
      return <StatusIndicator type={"error"}>Rework</StatusIndicator>;
    case "REJECTED":
      return <StatusIndicator type={"error"}>Rejected</StatusIndicator>;
    case "APPROVED":
      return <StatusIndicator type={"success"}>Approved</StatusIndicator>;
    case "APPROVED_BY_PARALLEL_APPROVER":
      return <StatusIndicator type={"success"}>-</StatusIndicator>;
    case "NOT_STARTED":
      return <StatusIndicator type={"pending"}>Queued</StatusIndicator>;

    default:
      return <StatusIndicator type={"in-progress"}>Unknown</StatusIndicator>;
  }
};

/**
 * This is to get the status indicator for scan jobs status. The backend status has been transformed for rendering.
 * @param param0
 * @returns
 */
export const TableItemScanJobStatus: React.FC<ITableItemStatusProps> = ({
  stringStatus,
}) => {
  switch (stringStatus) {
    //approval review table approver status
    case "Not started":
      return <StatusIndicator type={"pending"}>Not started</StatusIndicator>;
    case "In progress":
      return (
        <StatusIndicator type={"in-progress"}>In progress</StatusIndicator>
      );
    case "Ready for review":
      return <StatusIndicator type={"info"}>Ready for review</StatusIndicator>;
    case "Review complete":
      return (
        <StatusIndicator type={"success"}>Review complete</StatusIndicator>
      );
    case "Failed":
      return <StatusIndicator type={"error"}>Failed</StatusIndicator>;
    default:
      return (
        <StatusIndicator type={"in-progress"}>In progress</StatusIndicator>
      );
  }
};

export default TableItemStatus;
